import React, { type FC, useEffect } from 'react';
import { connect } from 'react-redux';
import { compose } from '@reduxjs/toolkit';

import type { BettingContextProps } from '../../../../../../types';
import type { ReduxState } from 'app/redux/types';

import { ContextNamespace } from 'app/constants/Context';
import { withinBettingContext } from 'app/contexts/betting';
import { MatchType } from 'app/lib/betting/betting-entities';
import useAnimationTrigger from 'app/lib/hooks/useAnimationTrigger';
import type { InnerLineEntry } from 'app/redux/betting-presentation/grid/selectors';

import SlotList from '../../../../../SlotList';
import InnerHeaderHeadToHead from '../InnerHeaderHeadToHead';

import styles from './InnerLine.module.scss';

// ---

type InnerLineProps = Pick<InnerLineEntry, 'id' | 'names' | 'kind' | 'matchId'> & {
  autoExpand?: boolean;
};

type StoreProps = {
  matchType: MatchType;
};

type ContextProps = {
  $betting: BettingContextProps;
};

const INNER_HEADER_BY_MATCH_TYPE = {
  [MatchType.OUTRIGHT]: (() => null) as FC,
  [MatchType.HEAD_TO_HEAD]: InnerHeaderHeadToHead,
};

const InnerLine: FC<InnerLineProps & StoreProps & ContextProps> = (props) => {
  const [isExpanded, setIsExpanded] = useAnimationTrigger(!props.autoExpand);
  const { names, id, matchId, matchType, kind } = props;

  useEffect(() => {
    if (matchType === MatchType.OUTRIGHT) {
      setIsExpanded(true);
    }
  }, [id]);

  const handleExpandClick = () => {
    setIsExpanded((value) => !value);
  };

  const InnerHeader = INNER_HEADER_BY_MATCH_TYPE[matchType];

  return (
    <>
      <InnerHeader
        expanded={isExpanded}
        lineNames={names}
        lineId={id}
        lineKind={kind}
        matchId={matchId}
        expandClick={handleExpandClick} />

      <div className={styles.slotContainer}>
        {isExpanded && matchId && id && (
          <SlotList matchId={matchId}
                    lineId={id}
                    isOutrightMatch={matchType === MatchType.OUTRIGHT} />
        )}
      </div>
    </>
  );
};

const createMapStateToProps = (
  state: ReduxState,
  {
    $betting: {
      namespace,
      content: {
        selectors: {
          matches,
        },
      },
      matchContent,
    },
    matchId,
  }: BettingContextProps & InnerLineProps,
) => (): StoreProps => {
  const isPrematch = namespace === ContextNamespace.PREMATCH;
  const prematchMatches = matchContent?.selectors?.matches;

  const match = (isPrematch && prematchMatches)
    ? prematchMatches(state).get(matchId)
    : matches(state).get(matchId);

  const matchType = match?.type || MatchType.HEAD_TO_HEAD;

  return {
    matchType,
  };
};

export default compose<FC<InnerLineProps>>(
  withinBettingContext,
  connect(createMapStateToProps),
)(InnerLine);
